import { toLanguageCode } from "../api/language";
import type { SectionLabels } from "../types";

/** The notice's two collapsible sections, in render order. */
export const NOTICE_SECTIONS = ["about", "dpo"] as const;

/**
 * SDK-owned UI copy for the section headers, keyed by BCP-47 code. The notice
 * record carries translations for its own content only, so these have to ship
 * with the SDK. "Grievance" matches the Privacy Center locale files.
 *
 * Bodo, Kashmiri, Manipuri and Santali are deliberately absent until a native
 * speaker supplies them: for those, `sectionLabelsFor` returns null and the
 * headers fall back to the notice's own translated text rather than English.
 */
const SECTION_LABELS: Record<string, SectionLabels> = {
  en: { about: "About this notice", dpo: "Grievances & your DPO" },
  as: { about: "এই জাননীৰ বিষয়ে", dpo: "অভিযোগ আৰু যোগাযোগ" },
  bn: { about: "এই বিজ্ঞপ্তি সম্পর্কে", dpo: "অভিযোগ ও যোগাযোগ" },
  doi: { about: "इस सूचना बारै", dpo: "शिकायत ते संपर्क" },
  gom: { about: "ह्या सुचनेविशीं", dpo: "तक्रार आनी संपर्क" },
  gu: { about: "આ સૂચના વિશે", dpo: "ફરિયાદ અને સંપર્ક" },
  hi: { about: "इस सूचना के बारे में", dpo: "शिकायत और संपर्क" },
  kn: { about: "ಈ ಸೂಚನೆಯ ಬಗ್ಗೆ", dpo: "ಕುಂದುಕೊರತೆ ಮತ್ತು ಸಂಪರ್ಕ" },
  mai: { about: "एहि सूचनाक विषयमे", dpo: "शिकायत आ संपर्क" },
  ml: { about: "ഈ അറിയിപ്പിനെക്കുറിച്ച്", dpo: "പരാതിയും ബന്ധപ്പെടലും" },
  mr: { about: "या सूचनेबद्दल", dpo: "तक्रार आणि संपर्क" },
  ne: { about: "यस सूचनाबारे", dpo: "गुनासो र सम्पर्क" },
  or: { about: "ଏହି ବିଜ୍ଞପ୍ତି ବିଷୟରେ", dpo: "ଅଭିଯୋଗ ଏବଂ ଯୋଗାଯୋଗ" },
  pa: { about: "ਇਸ ਸੂਚਨਾ ਬਾਰੇ", dpo: "ਸ਼ਿਕਾਇਤ ਅਤੇ ਸੰਪਰਕ" },
  sa: { about: "अस्याः सूचनायाः विषये", dpo: "परिवादः सम्पर्कश्च" },
  sd: { about: "هن نوٽيس بابت", dpo: "شڪايت ۽ رابطو" },
  ta: { about: "இந்த அறிவிப்பு பற்றி", dpo: "புகார் மற்றும் தொடர்பு" },
  te: { about: "ఈ నోటీసు గురించి", dpo: "ఫిర్యాదు మరియు సంప్రదింపు" },
  ur: { about: "اس نوٹس کے بارے میں", dpo: "شکایت اور رابطہ" },
};

/** Last resort when the notice has no copy of its own to title a section with. */
export const ENGLISH_SECTION_LABELS: SectionLabels = SECTION_LABELS.en;

/** Labels for a notice language (display name or code), or null if unsupported. */
export const sectionLabelsFor = (language: string): SectionLabels | null => {
  const code = toLanguageCode(language);
  if (!code) {
    return null;
  }
  const lower = code.toLowerCase();
  return (
    SECTION_LABELS[lower] ?? SECTION_LABELS[lower.split("-")[0]] ?? null
  );
};
